import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { CatalogIndexPage } from '@backstage/plugin-catalog';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import {
  catalogUnprocessedEntitiesPlugin,
  UnprocessedEntitiesContent,
} from '@backstage/plugin-catalog-unprocessed-entities';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { orgPlugin } from '@backstage/plugin-org';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import {
  CoderProvider,
  SynechronNavigation,
} from '@internal/backstage-plugin-synechron-navigation-react';
import { Navigate, Route } from 'react-router-dom';
import { apis } from './apis';
import { entityPage } from './components/entities/EntityPage';

import type { ReactNode } from 'react';
import {
  attachComponentData,
  getComponentData,
} from '@backstage/core-plugin-api';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
  convertLegacyPlugin,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import { createApp } from '@backstage/frontend-defaults';
import {
  AppRootElementBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import visualizerPlugin from '@backstage/plugin-app-visualizer';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  ConfigContent,
  DevToolsLayout,
  DevToolsPage,
  InfoContent,
} from '@backstage/plugin-devtools';
import { VisitListener } from '@backstage/plugin-home';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { HomePage } from './components/home/HomePage';
import { NotificationsPage } from '@backstage/plugin-notifications';

import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import {
  ScaffolderFieldExtensions,
  TemplateGroupFilter,
} from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import { SignalsDisplay, signalsPlugin } from '@backstage/plugin-signals';
import { InfraWalletPage } from '@electrolux-oss/plugin-infrawallet';
import { SynechronSignInPage } from '@internal/backstage-plugin-synechron-signin-react';
import {
  darkSynechronTheme,
  lightSynechronTheme,
} from '@internal/backstage-plugin-synechron-theme-react';
import { genCatalogTableColumns } from './components/catalog/table/columns/genCatalogTableColumns';
import { genDocsTableColumns } from './components/catalog/table/columns/genDocsTableColumns';
import { TableWithInjectedStyles } from './components/catalog/table/TableWithInjectedStyles';
import { SynechronGithubPickerFieldExtension } from './components/scaffolder/fields/SynechronGithubRepoPicker';
import { searchPage } from './components/search/SearchPage';

/**
 * Internal plugins migrated to the new frontend system. Each one owns its own
 * path, route ref and permission gate, so the app no longer declares routes for
 * them.
 */
import agenticMaturityReviewPlugin from '@internal/backstage-plugin-agentic-maturity-review/alpha';
import aiGraPlugin from '@internal/backstage-plugin-ai-gra/alpha';
import claudeUsagePlugin from '@internal/backstage-plugin-claude-usage/alpha';
import newStarterOnboardingPlugin from '@internal/backstage-plugin-new-starter-onboarding/alpha';
import synechronAppsPlugin from '@internal/backstage-plugin-synechron-apps/alpha';

const tagFilter = (
  tag: string,
): ((entity: TemplateEntityV1beta3) => boolean) => {
  // Filter out templates that are tagged with "experimental"
  const skip: string[] = ['experimental', 'beta']; // continue adding more tags to skip from the list
  return (entity: TemplateEntityV1beta3) =>
    !!entity?.metadata?.tags?.includes(tag) &&
    !skip.some(skipTag => entity?.metadata?.tags?.includes(skipTag));
};

const templateGroups = (): TemplateGroupFilter[] => [
  {
    title: 'Terraform',
    filter: tagFilter('terraform'),
  },
  {
    title: 'Python',
    filter: tagFilter('python'),
  },
  {
    title: 'Kubernetes',
    filter: tagFilter('kubernetes'),
  },
  {
    title: 'TechDocs',
    filter: tagFilter('techdocs'),
  },
];

/**
 * Wraps a plugin page in additional markup while keeping the route bound to its
 * plugin.
 *
 * `collectLegacyRoutes` reads `core.plugin` and `core.mountPoint` straight off a
 * route's `element` and does not look inside wrappers. Passing a wrapped element
 * (e.g. `<RequirePermission><CatalogImportPage /></RequirePermission>`) therefore
 * detaches the route from its plugin and leaves its routeRef without a path,
 * which breaks `useRouteRef` consumers such as the nav bar's Catalog button as
 * well as any `bindRoutes` target. Re-attaching the page's routing metadata to
 * the wrapper keeps both working.
 */
const wrappedRoute = (
  page: JSX.Element,
  wrap: (page: JSX.Element) => JSX.Element,
): JSX.Element => {
  const RouteElement = () => wrap(page);
  for (const type of ['core.plugin', 'core.mountPoint']) {
    const data = getComponentData<unknown>(page, type);
    if (data !== undefined) {
      attachComponentData(RouteElement, type, data);
    }
  }
  return <RouteElement />;
};

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route
      path="/systems"
      element={<Navigate to="/catalog?filters%5Bkind%5D=system" />}
    />
    <Route
      path="/components"
      element={<Navigate to="/catalog?filters%5Bkind%5D=component" />}
    />

    <Route
      path="/infrawallet"
      element={wrappedRoute(
        <InfraWalletPage title="Cloud Costs" subTitle="InfraWallet" />,
        page => (
          <RequirePermission permission={devToolsAdministerPermission}>
            {page}
          </RequirePermission>
        ),
      )}
    />

    <Route
      path="/docs"
      element={wrappedRoute(
        <TechDocsIndexPage initialFilter="all" columns={genDocsTableColumns()} />,
        page => <TableWithInjectedStyles>{page}</TableWithInjectedStyles>,
      )}
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={<ScaffolderPage groups={templateGroups()} />}
    >
      <ScaffolderFieldExtensions>
        <SynechronGithubPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route
      path="/api-docs"
      element={wrappedRoute(
        <ApiExplorerPage
          initiallySelectedFilter="all"
          // @ts-ignore - ApiExplorerPage DOES accept a function for columns just like the catalog page, but it's not typed
          columns={genCatalogTableColumns}
        />,
        page => <TableWithInjectedStyles>{page}</TableWithInjectedStyles>,
      )}
    />
    <Route
      path="/catalog-import"
      element={wrappedRoute(<CatalogImportPage />, page => (
        <RequirePermission permission={catalogEntityCreatePermission}>
          {page}
        </RequirePermission>
      ))}
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/homepage" element={<HomePage />} />
    <Route
      path="/devtools"
      element={wrappedRoute(<DevToolsPage />, page => (
        <RequirePermission permission={devToolsAdministerPermission}>
          {page}
        </RequirePermission>
      ))}
    >
      <DevToolsLayout>
        <DevToolsLayout.Route path="info" title="Info">
          <InfoContent />
        </DevToolsLayout.Route>
        <DevToolsLayout.Route path="config" title="Config">
          <ConfigContent />
        </DevToolsLayout.Route>
        <DevToolsLayout.Route
          path="unprocessed-entities"
          title="Unprocessed Entities"
        >
          <UnprocessedEntitiesContent />
        </DevToolsLayout.Route>
      </DevToolsLayout>
    </Route>
  </FlatRoutes>
);

/**
 * The app shell: the provider and layout tree that wraps the routed content.
 *
 * `convertLegacyAppRoot` identifies the single element between `AppRouter` and
 * `FlatRoutes` as the app's "Root" and re-renders it with the new frontend
 * system's routed content in place of `children`. Keeping the providers and the
 * sidebar inside one component means that whole tree is preserved verbatim —
 * had they been passed as sibling elements the converter would have discarded
 * everything except the outermost one.
 */
const AppShell = ({ children }: { children?: ReactNode }) => (
  <CoderProvider>
    <VisitListener />
    <SynechronNavigation>{children}</SynechronNavigation>
  </CoderProvider>
);

// The legacy `createApp` options, converted into a single frontend module.
const legacyOptionsModule = convertLegacyAppOptions({
  apis,
  featureFlags: [
    {
      pluginId: '',
      name: 'experimental-colours',
      description: 'Enables some experimental colours and styles',
    },
  ],
  components: {
    SignInPage: SynechronSignInPage,
  },
  themes: [darkSynechronTheme, lightSynechronTheme],
});

/**
 * The legacy element tree, converted into plugins/modules for the new system.
 *
 * Passing `entityPage` splits the hand-built `EntitySwitch` tree into individual
 * catalog entity card/content extensions, preserving the per-kind layout and the
 * `if` predicates (`EntitySwitch`'s first-match-wins semantics are replayed as
 * composed filters). The legacy `/catalog` routes are gone from `routes` above,
 * since the catalog plugin below now owns them.
 */
const legacyRootFeatures = convertLegacyAppRoot(
  <AppRouter>
    <AppShell>{routes}</AppShell>
  </AppRouter>,
  { entityPage },
);

/**
 * The catalog index page is customised (column set, default filter and injected
 * table styles), so the plugin's own page extension is overridden rather than
 * left at its default.
 */
const catalogPluginWithOverrides = catalogPlugin.withOverrides({
  extensions: [
    catalogPlugin.getExtension('page:catalog').override({
      params: {
        // CatalogIndexPage renders its own Header; suppress the default one.
        noHeader: true,
        loader: async () => (
          <TableWithInjectedStyles>
            <CatalogIndexPage
              initiallySelectedFilter="all"
              columns={genCatalogTableColumns}
            />
          </TableWithInjectedStyles>
        ),
      },
    }),
  ],
});

/**
 * `AlertDisplay` and `OAuthRequestDialog` are built-in extensions in the new
 * frontend system, so they are no longer rendered by this app. `SignalsDisplay`
 * has no built-in equivalent yet, so it is registered as an app root element.
 */
const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootElementBlueprint.make({
      name: 'signals-display',
      params: { element: <SignalsDisplay /> },
    }),
  ],
});

/**
 * Plugins that aren't reachable from the route tree, so `convertLegacyAppRoot`
 * can't discover them. `convertLegacyPlugin` registers their APIs under their own
 * plugin id — note that passing them via `convertLegacyAppOptions({ plugins })`
 * instead would register those APIs under `app` and fail at startup with
 * API_FACTORY_CONFLICT.
 *
 * `grafanaPlugin` and `notificationsPlugin` are deliberately absent: they are
 * already discovered from the route tree (the Grafana entity cards and the
 * `/notifications` route), and registering them again causes that same conflict.
 */
const unreachablePlugins = [
  convertLegacyPlugin(signalsPlugin, { extensions: [] }),
  convertLegacyPlugin(catalogUnprocessedEntitiesPlugin, { extensions: [] }),
];

/**
 * The app visualizer renders the whole extension tree. It is valuable while the
 * migration to the new frontend system is still in progress, but it is a
 * diagnostic tool rather than something users should be able to navigate to, so
 * it is only registered outside production builds.
 */
const diagnosticFeatures =
  process.env.NODE_ENV === 'production' ? [] : [visualizerPlugin];

// Internal plugins, now native to the new frontend system.
const internalPlugins = [
  agenticMaturityReviewPlugin,
  aiGraPlugin,
  claudeUsagePlugin,
  newStarterOnboardingPlugin,
  synechronAppsPlugin,
];

const app = createApp({
  features: [
    legacyOptionsModule,
    ...legacyRootFeatures,
    ...unreachablePlugins,
    ...internalPlugins,
    catalogPluginWithOverrides,
    appModule,
    ...diagnosticFeatures,
  ],
  bindRoutes({ bind }) {
    // The catalog is native now, so its refs need no conversion.
    bind(catalogPlugin.externalRoutes, {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(
        catalogImportPlugin.routes.importPage,
      ),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

export default app.createRoot();
